import { useTranslation } from 'react-i18next'
import { formatNumbers } from 'core/helpers/format-numbers'
import { useEffect, useMemo, useRef, useState } from 'react'
import { lock, unlock } from 'tua-body-scroll-lock'
import cn from 'classnames'

import energyPic from './assets/images/prize-energy.png'
import giftPic from './assets/images/prize-gift.png'
import coinPic from './assets/images/prize-coin.png'
import codePic from './assets/images/prize-promo-code.png'

import c from './assets/sass/prize.module.scss'
import { prizeTypes } from './constants/prize'
import { copyToClipboard } from '../../../core/helpers/copy-to-clipboard'
import {
    getPromoUrl,
    getPromoTypeFromPrizeType,
} from '../../../core/helpers/promo-url'
import { useAutoTranslate } from '../../../core/hooks/useAutoTranslate'

// Универсальные картинки для промокодов
const universalCasePic = '/images/Frame 1.png'
const wheelPic = '/images/Frame 2.png'

export const Prize = ({ data, onClose, show }) => {
    const { t } = useTranslation()
    const prizeRef = useRef()
    const [isCopied, setIsCopied] = useState(false)
    
    // Автоперевод для динамического описания с сервера
    const translatedDescription = useAutoTranslate(data?.description || '')

    useEffect(() => {
        if (prizeRef.current) {
            if (show) {
                lock(prizeRef.current, { reserveScrollBarGap: true })
            } else {
                unlock(prizeRef.current)
            }
        }
    }, [show])

    const handleConfirm = () => {
        console.log('Prize confirmed')

        if (onClose) {
            unlock(prizeRef.current)
            onClose()
        }
    }

    const handleGoToSite = () => {
        if (!data?.promoCode) {
            console.warn('No promo code available')
            return
        }

        const promoType = getPromoTypeFromPrizeType(data.type)
        const url = getPromoUrl(data.promoCode, promoType)

        console.log('🔗 Opening promo URL:', {
            type: data.type,
            promoType,
            url,
        })

        try {
            if (window?.Telegram?.WebApp?.openLink) {
                window.Telegram.WebApp.openLink(url)
            } else {
                window.open(url, '_blank')
            }
        } catch (e) {
            console.error('Error opening link:', e)
            window.open(url, '_blank')
        }

        if (onClose) {
            unlock(prizeRef.current)
            onClose()
        }
    }

    const currentContent = useMemo(() => {
        if (data.type === prizeTypes.energy) {
            return {
                content: (
                    <div className={c.row}>
                        <div className={c.prize}>
                            <img src={energyPic} alt="Energy" />
                        </div>
                        <div className={c.prizeText}>
                            <span className={c.prizeTitle}>
                                {t('advent.prize.received')}
                            </span>
                            <span className={c.prizeType}>
                                {t('advent.prize.energy')}
                            </span>
                            <span className={cn(c.prizeCounter, c.energy)}>
                                {formatNumbers(data.amount || 0)}
                            </span>
                        </div>
                    </div>
                ),
            }
        }

        if (data.type === prizeTypes.case) {
            return {
                content: (
                    <div className={c.row}>
                        <div className={c.prize}>
                            <img src={universalCasePic} alt="Case" />
                        </div>
                        <div className={c.prizeText}>
                            <span className={c.prizeTitle}>
                                {t('advent.prize.received')}
                            </span>
                            <span className={c.prizeType}>
                                {t('advent.prize.caseCode')}
                            </span>
                            <span className={c.prizeTitle}>
                                {translatedDescription ||
                                    t('advent.prize.caseDescription')}
                            </span>
                        </div>
                    </div>
                ),
                promoCodeSubtitle: t('advent.prize.promoCodeCase'),
            }
        }

        if (data.type === prizeTypes.coin) {
            return {
                content: (
                    <div className={c.row}>
                        <div className={c.prize}>
                            <img src={coinPic} alt="Coin" />
                        </div>
                        <div className={c.prizeText}>
                            <span className={c.prizeTitle}>
                                {t('advent.prize.received')}
                            </span>
                            <span className={c.prizeType}>
                                {t('advent.prize.coins')}
                            </span>
                            <span className={cn(c.prizeCounter, c.coin)}>
                                {formatNumbers(data.amount || 0)}
                            </span>
                        </div>
                    </div>
                ),
            }
        }

        if (data.type === prizeTypes.depositCode) {
            return {
                content: (
                    <div className={c.row}>
                        <div className={c.prize}>
                            <img src={codePic} alt="Code" />
                        </div>
                        <div className={c.prizeText}>
                            <span className={c.prizeTitle}>
                                {t('advent.prize.received')}
                            </span>
                            <span className={c.prizeType}>
                                {t('advent.prize.depositCode')}
                            </span>
                            <span className={c.prizeTitle}>
                                {translatedDescription ||
                                    t('advent.prize.depositDescription')}
                            </span>
                        </div>
                    </div>
                ),
                note: (
                    <p
                        className={c.prizeNote}
                        dangerouslySetInnerHTML={{
                            __html: t('advent.prize.depositNote'),
                        }}
                    ></p>
                ),
                promoCodeSubtitle: t('advent.prize.promoCodeDeposit'),
            }
        }

        if (data.type === prizeTypes.wheelCode) {
            return {
                content: (
                    <div className={c.row}>
                        <div className={c.prize}>
                            <img src={wheelPic} alt="Wheel" />
                        </div>
                        <div className={c.prizeText}>
                            <span className={c.prizeTitle}>
                                {t('advent.prize.received')}
                            </span>
                            <span className={c.prizeType}>
                                {t('advent.prize.wheelCode')}
                            </span>
                            <span className={c.prizeTitle}>
                                {translatedDescription ||
                                    t('advent.prize.depositDescription')}
                            </span>
                        </div>
                    </div>
                ),
                promoCodeSubtitle: t('advent.prize.promoCodeWheel'),
            }
        }

        if (data.type === prizeTypes.depositFreeCase) {
            return {
                content: (
                    <div className={c.row}>
                        <div className={c.prize}>
                            <img src={universalCasePic} alt="Case" />
                        </div>
                        <div className={c.prizeText}>
                            <span className={c.prizeTitle}>
                                {t('advent.prize.received')}
                            </span>
                            <span
                                className={c.prizeType}
                                dangerouslySetInnerHTML={{
                                    __html: t('advent.prize.depositFreeCase'),
                                }}
                            ></span>
                            <span className={c.prizeTitle}>
                                {data.description ||
                                    t('advent.prize.depositDescription')}
                            </span>
                        </div>
                    </div>
                ),
                note: (
                    <p
                        className={c.prizeNote}
                        dangerouslySetInnerHTML={{
                            __html: t('advent.prize.depositNote'),
                        }}
                    ></p>
                ),
                promoCodeSubtitle: t('advent.prize.promoCodeCase'),
            }
        }

        if (data.type === prizeTypes.xpCode) {
            return {
                content: (
                    <div className={c.row}>
                        <div className={c.prize}>
                            <img src={codePic} alt="Code" />
                        </div>
                        <div className={c.prizeText}>
                            <span className={c.prizeTitle}>
                                {t('advent.prize.received')}
                            </span>
                            <span
                                className={c.prizeType}
                                dangerouslySetInnerHTML={{
                                    __html: t('advent.prize.xpCode'),
                                }}
                            ></span>
                            <span className={c.prizeTitle}>
                                {data.description ||
                                    t('advent.prize.depositDescription')}
                            </span>
                        </div>
                    </div>
                ),
                promoCodeSubtitle: t('advent.prize.promoCodeXP'),
            }
        }

        if (data.type === prizeTypes.skinCode) {
            return {
                content: (
                    <div className={c.row}>
                        <div className={c.prize}>
                            <img src={codePic} alt="Code" />
                        </div>
                        <div className={c.prizeText}>
                            <span className={c.prizeTitle}>
                                {t('advent.prize.received')}
                            </span>
                            <span
                                className={c.prizeType}
                                dangerouslySetInnerHTML={{
                                    __html: t('advent.prize.skinCode'),
                                }}
                            ></span>
                            <span className={c.prizeTitle}>
                                {translatedDescription ||
                                    t('advent.prize.skinDescription')}
                            </span>
                        </div>
                    </div>
                ),
                promoCodeSubtitle: t('advent.prize.promoCodeSkin'),
            }
        }

        if (data.type === prizeTypes.balanceCode) {
            return {
                content: (
                    <div className={c.row}>
                        <div className={c.prize}>
                            <img src={codePic} alt="Code" />
                        </div>
                        <div className={c.prizeText}>
                            <span className={c.prizeTitle}>
                                {t('advent.prize.received')}
                            </span>
                            <span className={c.prizeType}>
                                {t('advent.prize.balanceCode')}
                            </span>
                            <span className={c.prizeTitle}>
                                {translatedDescription ||
                                    t('advent.prize.depositDescription')}
                            </span>
                        </div>
                    </div>
                ),
                promoCodeSubtitle: t('advent.prize.promoCodeBalance'),
            }
        }

        if (data.type === prizeTypes.gift) {
            return {
                content: (
                    <div className={c.row}>
                        <div className={c.prize}>
                            <img src={giftPic} alt="Gift" />
                        </div>
                        <div className={c.prizeText}>
                            <span className={c.prizeTitle}>
                                {t('advent.prize.received')}
                            </span>
                            <span className={c.prizeType}>
                                {t('advent.prize.gift')}
                            </span>
                            <span className={c.prizeTitle}>
                                {translatedDescription ||
                                    t('advent.prize.depositDescription')}
                            </span>
                        </div>
                    </div>
                ),
                promoCodeSubtitle: t('advent.prize.promoCodeGeneric'),
            }
        }

        return {
            content: (
                <div className={c.row}>
                    <div className={c.prize}>
                        <img src={codePic} alt="Code" />
                    </div>
                    <div className={c.prizeText}>
                        <span className={c.prizeTitle}>
                            {t('advent.prize.received')}
                        </span>
                        <span className={c.prizeType}>
                            {t('advent.prize.promoCode')}
                        </span>
                        <span className={c.prizeTitle}>
                            {data.description ||
                                t('advent.prize.depositDescription')}
                        </span>
                    </div>
                </div>
            ),
            promoCodeSubtitle: t('advent.prize.promoCodeGeneric'),
        }
    }, [data, t, translatedDescription])

    if (!data) return null

    const handleCopy = (code) => {
        copyToClipboard(code, t('common.copy.success'))
        setIsCopied(true)
        setTimeout(() => setIsCopied(false), 2000)
    }

    // Определяем нужна ли кнопка "Забрать на сайте"
    const needsSiteButton =
        data?.promoCode &&
        [
            prizeTypes.case,
            prizeTypes.depositCode,
            prizeTypes.skinCode,
            prizeTypes.wheelCode,
            prizeTypes.depositFreeCase,
            prizeTypes.xpCode,
            prizeTypes.balanceCode,
            prizeTypes.gift,
        ].includes(data.type)

    return (
        <div ref={prizeRef} className={c.component}>
            {currentContent.content}
            <div className={c.bottom}>
                {currentContent.note && currentContent.note}
                {data.promoCode && (
                    <div className={c.code}>
                        <div className={c.codeRow}>
                            <span className={c.codeText}>{data.promoCode}</span>
                            <span className={c.codeTitle}>
                                {currentContent.promoCodeSubtitle}
                            </span>
                        </div>
                        <button
                            type="button"
                            className={c.codeButton}
                            onClick={() => handleCopy(data.promoCode)}
                        >
                            <span>
                                {isCopied
                                    ? t('advent.prize.copied')
                                    : t('advent.prize.copy')}
                            </span>
                        </button>
                    </div>
                )}
                {needsSiteButton ? (
                    <button
                        type="button"
                        className={c.button}
                        onClick={handleGoToSite}
                    >
                        <span>{t('advent.prize.getOnSite')}</span>
                    </button>
                ) : (
                    <button
                        type="button"
                        className={c.button}
                        onClick={handleConfirm}
                    >
                        <span>{t('advent.prize.confirm')}</span>
                    </button>
                )}
            </div>
        </div>
    )
}
